import {
  deleteDocument,
  getDocumentsWithSettings,
  signDocument,
  signPage,
  uploadDocument,
  signBox,
  getSignatureImages,
} from 'portal-modules/documents/api/api'
import { call, put, takeLatest } from 'redux-saga/effects'
import createFormAction from 'portal-modules/common/redux/create-form-action'
import createRequestSaga from 'portal-modules/common/redux/create-request-saga'
import {
  CHANGE_USER_ACCOUNT,
  LOGOUT,
} from 'portal-modules/auth/redux/modules/user'
import { addCustomContent } from 'portal-modules/common/redux/modules/customContent'
import { addSettings } from 'portal-modules/common/redux/modules/settings'
import { DOCUMENT_TYPES } from '../../constants/types'

// action types
const RECEIVE_DELETE = 'rp/documents/RECEIVE_DELETE'
const RECEIVE_DOCUMENTS = 'rp/documents/RECEIVE_DOCUMENTS'
const RECEIVE_SIGN_DOCUMENT = 'rp/documents/RECEIVE_SIGN_DOCUMENT'
const RECEIVE_SIGN_PACKET_DOCUMENT = 'rp/documents/RECEIVE_SIGN_PACKET_DOCUMENT'
const RECEIVE_SIGN_PAGE = 'rp/documents/RECEIVE_SIGN_PAGE'
const RECEIVE_SIGN_BOX = 'rp/documents/RECEIVE_SIGN_BOX'
const RECEIVE_UPLOAD = 'rp/documents/RECEIVE_UPLOAD'
const RECEIVE_SIGNATURE_IMAGES = 'rp/documents/RECEIVE_SIGNATURE_IMAGES'
const REQUEST_DELETE = 'rp/documents/REQUEST_DELETE'
const REQUEST_DOCUMENTS = 'rp/documents/REQUEST_DOCUMENTS'
const REQUEST_SIGN_DOCUMENT = 'rp/documents/REQUEST_SIGN_DOCUMENT'
const REQUEST_SIGN_PACKET_DOCUMENT = 'rp/documents/REQUEST_SIGN_PACKET_DOCUMENT'
const REQUEST_SIGN_PAGE = 'rp/documents/REQUEST_SIGN_PAGE'
const REQUEST_SIGN_BOX = 'rp/documents/REQUEST_SIGN_BOX'
const REQUEST_UPLOAD = 'rp/documents/REQUEST_UPLOAD'
const REQUEST_SIGNATURE_IMAGES = 'rp/documents/REQUEST_SIGNATURE_IMAGES'

// action creators
export const requestDocuments = payload => ({
  type: REQUEST_DOCUMENTS,
  payload,
})

export const receiveDocuments = payload => ({
  type: RECEIVE_DOCUMENTS,
  payload,
})

export const requestUpload = createFormAction(REQUEST_UPLOAD)

export const receiveUpload = payload => ({
  type: RECEIVE_UPLOAD,
  payload,
})

export const requestDelete = payload => ({
  type: REQUEST_DELETE,
  payload,
})

export const receiveDelete = payload => ({
  type: RECEIVE_DELETE,
  payload,
})

export const requestSignDocument = ({ payload, callback }) => ({
  type: REQUEST_SIGN_DOCUMENT,
  payload,
  callback,
})
export const requestSignPacketDocument = ({ payload, callback }) => ({
  type: REQUEST_SIGN_PACKET_DOCUMENT,
  payload,
  callback,
})

export const receiveSignDocument = payload => ({
  type: RECEIVE_SIGN_DOCUMENT,
  payload,
})

export const receiveSignPacketDocument = payload => ({
  type: RECEIVE_SIGN_PACKET_DOCUMENT,
  payload,
})

export const receiveSignPage = payload => ({
  type: RECEIVE_SIGN_PAGE,
  payload,
})

export const requestSignPage = ({ payload, callback }) => ({
  type: REQUEST_SIGN_PAGE,
  payload,
  callback,
})

export const requestSignBox = ({ payload, callback }) => ({
  type: REQUEST_SIGN_BOX,
  payload,
  callback,
})

export const receiveSignBox = payload => ({
  type: RECEIVE_SIGN_BOX,
  payload,
})

export const requestSignatureImages = ({ payload, callback }) => ({
  type: REQUEST_SIGNATURE_IMAGES,
  payload,
  callback,
})

export const receiveSignatureImages = payload => ({
  type: RECEIVE_SIGNATURE_IMAGES,
  payload,
})

// reducer
export default (state = {}, { payload, type }) => {
  switch (type) {
    case RECEIVE_DELETE:
      return {
        ...state,
        archive: Object.keys(state.archive).filter(key => key !== payload),
      }
    case REQUEST_DOCUMENTS:
      return { ...state, ...payload, document_received: false }
    case RECEIVE_DOCUMENTS:
      return { ...state, ...payload, document_received: true }
    case RECEIVE_SIGN_DOCUMENT:
      return {
        ...state,
        needsAction: Object.values(state.needsAction || {}).reduce(
          (obj, document) =>
            document && document.id !== payload.id
              ? { ...obj, [document.id]: document }
              : obj,
          {},
        ),
        archive: { ...state.archive, [payload.id]: payload },
      }
    case RECEIVE_SIGN_PACKET_DOCUMENT:
      return {
        ...state,
        needsAction: Object.values(state.needsAction || {}).reduce(
          (obj, document) => {
            if (document.id != payload.document_packet.parent_file_id) {
              obj = { ...obj, [document.id]: document }
            } else if (
              payload.document_packet.child_file_details.find(child_file => {
                return child_file.signed_on == null
              })
            ) {
              obj = { ...obj, [document.id]: document }
            }
            return obj
          },
          {},
        ),
        document_packets: {
          ...state.document_packets,
          [payload.document_packet.parent_file_id]: payload,
        },
      }
    case RECEIVE_SIGN_PAGE:
      return {
        ...state,
        needsAction: Object.values(state.needsAction || {}).reduce(
          (obj, document) =>
            document &&
            (document.id !== payload.id ||
              (document.id == payload.id &&
                payload.document_packet &&
                payload.document_packet.signed_on == null))
              ? { ...obj, [document.id]: document }
              : obj,
          {},
        ),
        ...(payload.document_packet
          ? {
              document_packets: {
                ...(state.document_packets || {}),
                [payload.id]: {
                  document_packet: { ...payload.document_packet },
                },
              },
            }
          : {}),
        archive: {
          ...state.archive,
          ...(!payload.document_packet ||
          payload.document_packet.signed_on != null
            ? { [payload.id]: payload }
            : {}),
        },
      }
    case RECEIVE_SIGN_BOX:
    case RECEIVE_SIGNATURE_IMAGES:
      return { ...state, ...payload }
    case RECEIVE_UPLOAD:
      return {
        ...state,
        archive: { ...state.archive, [payload.id]: payload },
      }
    case CHANGE_USER_ACCOUNT:
    case LOGOUT:
      return {}
    default:
      return state
  }
}

// sagas
function* postDeleteDocument({ payload }) {
  yield call(deleteDocument, payload)
  yield put(receiveDelete(payload.id))
}

function* watchRequestDelete() {
  yield takeLatest(
    REQUEST_DELETE,
    createRequestSaga(postDeleteDocument, {
      key: 'documents',
      message: __('Deleting document'),
      successMessage: __('Document deleted'),
      write: true,
    }),
  )
}

function* fetchDocuments({ payload }) {
  const { documents = [], custom_documents = [], settings } = yield call(
    getDocumentsWithSettings,
    payload,
  )
  let actions = [],
    needsAction = {},
    archive = {},
    document_packets = {}

  custom_documents.forEach(element => {
    if (element.custom_document_type_id == DOCUMENT_TYPES.WEB_PAGE) {
      archive[getUniqIndex(archive, element)] = element
    } else if (element?.is_submitted && !element?.redirect_url) {
      archive[getUniqIndex(archive, element)] = element
    } else {
      needsAction[getUniqIndex(needsAction, element)] = element
    }
  })

  function getUniqIndex(obj, element) {
    if (element?.maintenance_request_id) {
      if (
        Object.keys(obj).includes(element.maintenance_request_id.toString())
      ) {
        return element.maintenance_request_id + '-' + element.id
      } else {
        return element.maintenance_request_id
      }
    } else {
      return element.id
    }
  }

  // Note:: keeping the following code as simple as possible as is will help devs to upderstand it easily when incorporating merge-field and box-sign features.
  // We may need to redefine how we store and use values
  function arrangeDocs(docs, isPacket, doc_packet) {
    docs.forEach(element =>
      element.requires_signing && element.show_in_list && !element.signed_on
        ? (needsAction[element.id] = element)
        : isPacket
        ? element.show_in_list && (archive[element.id] = element)
        : (archive[element.id] = element),
    )
    if (isPacket) {
      document_packets[doc_packet.document_packet.parent_file_id] = doc_packet
    }
  }

  documents.forEach(element => {
    element.hasOwnProperty('document_packet')
      ? arrangeDocs(
          element.document_packet.packet_documents || [],
          true,
          element,
        )
      : arrangeDocs([element])
  })

  actions.push(receiveDocuments({ needsAction, archive, document_packets }))

  if (settings) {
    actions.push(addCustomContent({ documents: settings.custom_text }))
    actions.push(
      addSettings({
        documents: {
          resident_upload_doc_types: settings.resident_upload_doc_types,
          electronic_terms: settings.electronic_terms,
          require_full_name: settings.require_full_name,
          media_library_path: settings.media_library_path,
        },
      }),
    )
  }
  yield actions.map(a => put(a))
}

function* watchRequestDocuments() {
  yield takeLatest(
    REQUEST_DOCUMENTS,
    createRequestSaga(fetchDocuments, {
      key: 'documents',
      message: __('Finding your documents'),
    }),
  )
}

function* postSignDocument({ payload, callback }) {
  const { document } = yield call(signDocument, payload)
  yield put(receiveSignDocument(document))
  typeof callback === 'function' && callback()
}

function* postSignPacketDocument({ payload, callback }) {
  const { document } = yield call(signDocument, payload)
  yield put(receiveSignPacketDocument(document))
  typeof callback === 'function' && callback()
}

function* watchRequestSignDocument() {
  yield takeLatest(
    REQUEST_SIGN_DOCUMENT,
    createRequestSaga(postSignDocument, {
      key: 'documents',
      message: __('Signing document'),
      successMessage: __('Document Signed'),
      write: true,
    }),
  )
}

function* watchRequestSignPacketDocument() {
  yield takeLatest(
    REQUEST_SIGN_PACKET_DOCUMENT,
    createRequestSaga(postSignPacketDocument, {
      key: 'documents',
      message: __('Signing document'),
      successMessage: __('Document Signed'),
      write: true,
    }),
  )
}

function* postSignPage({ payload, callback }) {
  const { document } = yield call(signPage, payload)
  yield put(receiveSignPage({ ...document, id: payload.id }))
  document && typeof callback === 'function' && callback()
}

function* watchRequestSignPage() {
  yield takeLatest(
    REQUEST_SIGN_PAGE,
    createRequestSaga(postSignPage, {
      key: 'documents',
      message: __('Signing page'),
      successMessage: __('Page Signed'),
      write: true,
    }),
  )
}

function* postUploadDocument({ payload }) {
  const formData = new FormData()

  formData.append('document_type_id', payload.document_type_id)
  if (payload.document_id) {
    formData.append('document_id', payload.document_id)
  }
  payload?.document?.map(image => {
    formData.append(`document`, image)
  })

  const data = yield call(uploadDocument, formData)
  yield put(receiveUpload(data))
}

function* watchRequestUpload() {
  yield takeLatest(
    REQUEST_UPLOAD,
    createRequestSaga(postUploadDocument, {
      key: 'documents',
      message: __('Uploading document'),
      successMessage: __('Document uploaded'),
      write: true,
    }),
  )
}

function* postSignBox({ payload, callback }) {
  const { is_signed } = yield call(signBox, payload)
  yield put(receiveSignBox(is_signed))

  is_signed && typeof callback === 'function' && callback(is_signed)
}

function* watchRequestSignBox() {
  yield takeLatest(
    REQUEST_SIGN_BOX,
    createRequestSaga(postSignBox, {
      key: 'documents',
      message: __('Adding signature'),
      successMessage: __('Signature added'),
      write: true,
    }),
  )
}

function* fetchSignatureImages({ payload, callback }) {
  const { result } = yield call(getSignatureImages, payload)
  yield put(receiveSignatureImages(result))
  result && typeof callback === 'function' && callback(result)
}

function* watchRequestSignatureImages() {
  yield takeLatest(
    REQUEST_SIGNATURE_IMAGES,
    createRequestSaga(fetchSignatureImages, {
      key: 'documents',
      showLoading: false,
    }),
  )
}

export const sagas = [
  watchRequestDelete,
  watchRequestDocuments,
  watchRequestSignDocument,
  watchRequestSignPacketDocument,
  watchRequestSignPage,
  watchRequestUpload,
  watchRequestSignBox,
  watchRequestSignatureImages,
]
